/**
 * Teacher registry endpoints — the ADDITIVE contract #7.5 (v1.3, task T.F6), talking to
 * the live `apps/api/src/teachers` module.
 *
 * The shapes are declared here instead of imported from `@cb-manager/shared` for the
 * reason `./venues` already states: the generated
 * `packages/shared/src/openapi/api-types.ts` is not re-exported through the package
 * barrel, so a workspace consumer cannot reach it. `apps/api/src/teachers/dto.ts`
 * documents the same 1:1 mirror on the server side — a divergence either way is a
 * contract bug.
 *
 * PII note: the roster carries ФИО/phone/email CLEAR for every role (the v1.1 owner
 * correction), so there is no reveal/mask state here — the only gate in this module is
 * the write one (MANAGER+ to create/edit, and since v1.9.12/T.Z80 to delete; a КУРАТОР
 * through its cabinet scope on all three), which mirrors the api.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { buildQuery, requestEnvelope } from './client';

/**
 * Mirrors `TeacherAttributes` (T.R7) — the sheet's free-form columns behind the contact
 * cell, stored and served verbatim. Kept as its own interface so the drawer can group
 * and label them without repeating nineteen literals.
 */
export interface TeacherAttributes {
  /** «Формат обучения» — «Офлайн\Онлайн». */
  learningFormat: string | null;
  /** «Эл. подпись педагога (да\нет)». */
  esignature: string | null;
  /** «Статус педагога (ТД, ГПХ, СЗ)». */
  employmentStatus: string | null;
  /** «КПК» — the да/нет answer. */
  kpkAttended: string | null;
  /** The date range the «КПК» cell carries, when it carries one. */
  kpkDates: string | null;
  /** «Отправлена ссылка …» — the two course URLs, multi-line. */
  courseLinksSent: string | null;
  /** «Подал заявку на сайте Педагог.13 DL 12/09». */
  appliedOnSite: string | null;
  /** «Сданы документы на КПК 12.09-15.09». */
  docsSubmitted: string | null;
  /** «Участие в реализации ДОП». */
  dopParticipation: string | null;
  /** «Наименование основного места работы, должность». */
  mainWorkplace: string | null;
  /** «Ученая степень и (или) ученое звание». */
  academicDegree: string | null;
  /** «Образование (заведение, специальность, год окончания)». */
  education: string | null;
  /** «Основание для привлечения к реализации ДОП». */
  engagementBasis: string | null;
  /** «Информация о ДПО по профилю… (за последние 3 года)». */
  dpoLast3Years: string | null;
  /** «Опыт работы в области ИТ, ИИ или робототехники…». */
  itExperience: string | null;
  /** «Отметка о полученном согласии на обработку персональных данных». */
  pdConsent: string | null;
  /** «Договор подписан Исполнителем». */
  contractSigned: string | null;
  /** «Ссылка на папку с документами на Диске». */
  docsFolderUrl: string | null;
  /** «Дата договора (инфо от аутсорса)». */
  contractDate: string | null;
}

/**
 * ADDITIVE (v1.9.17, T.Z89) — the verification state of a row, mirrored from the
 * contract's `TeacherDto.status`. The RU wording lives in the web model
 * (`statusLabel`), the same split every UPPER_SNAKE enum of this repo keeps.
 */
export type TeacherStatus = 'CONFIRMED' | 'UNCONFIRMED' | 'FOR_DELETION';

/**
 * ADDITIVE (v1.9.17, T.Z90) — where the row came from, the provenance the «добавлены
 * кураторами» chip counts.
 */
export type TeacherCreatedVia = 'REGISTRY' | 'STAFF' | 'CURATOR';

/**
 * Mirrors `TeacherDto`. `venueName` is resolved server-side (join field).
 *
 * ADDITIVE (v1.9.17, T.Z91) — on a КУРАТОР session the api OMITS the ten attribute
 * cells of «КПК / Договор / Документы и согласия» (`CuratorTeacherDto` below); the
 * full shape stays the static type (the page renders by ROLE, never by sniffing the
 * row, so a curator's render path never reads those keys — at runtime they are simply
 * absent, and every reader here folds `undefined` the same way as `null`).
 */
export interface TeacherDto extends TeacherAttributes {
  id: number;
  /** FK Venue.id — the venue the teacher is attached to; PATCH it to re-assign. */
  venueId: number;
  /** Resolved `Venue.name`. */
  venueName: string;
  fullName: string;
  /** Verbatim (the registry keeps whatever spelling the source sheet wrote). */
  phone: string | null;
  /** Lowercased by the api. */
  email: string | null;
  /** ADDITIVE (v1.9.17, T.Z89) — «подтверждён» / «не подтверждён» / «на удаление». */
  status: TeacherStatus;
  /** ADDITIVE (v1.9.17, T.Z90) — REGISTRY | STAFF | CURATOR (the provenance). */
  createdVia: TeacherCreatedVia;
}

/** The ten cells of «КПК / Договор / Документы и согласия» a КУРАТОР must not see. */
export type CuratorHiddenAttributeKey =
  | 'kpkAttended'
  | 'kpkDates'
  | 'courseLinksSent'
  | 'appliedOnSite'
  | 'docsSubmitted'
  | 'contractSigned'
  | 'contractDate'
  | 'esignature'
  | 'pdConsent'
  | 'docsFolderUrl';

/**
 * The same block, as the iterable the model's visibility cut and the drafts read —
 * the mirror of `CURATOR_HIDDEN_ATTRIBUTE_KEYS` in `apps/api/src/teachers/dto.ts`.
 */
export const CURATOR_HIDDEN_ATTRIBUTE_KEYS = [
  'kpkAttended',
  'kpkDates',
  'courseLinksSent',
  'appliedOnSite',
  'docsSubmitted',
  'contractSigned',
  'contractDate',
  'esignature',
  'pdConsent',
  'docsFolderUrl',
] as const satisfies readonly CuratorHiddenAttributeKey[];

/** ADDITIVE (v1.9.17, T.Z91) — the КУРАТОР-session row: the full DTO minus the block. */
export type CuratorTeacherDto = Omit<TeacherDto, CuratorHiddenAttributeKey>;

/** ADDITIVE (v1.9.17, T.Z90) — the chips' funnel, mirrored from `meta.counts`. */
export interface TeacherListCounts {
  total: number;
  confirmed: number;
  unconfirmed: number;
  forDeletion: number;
  curatorAdded: number;
}

/** The contract's `TeacherPaginationMeta` (`counts` is ADDITIVE v1.9.17, optional). */
export interface TeacherListMeta {
  page: number;
  pageSize: number;
  total: number;
  counts?: TeacherListCounts;
}

/** `#7.5 list` — `{ data: { teachers: [...] }, meta }`. */
export interface TeacherListResult {
  teachers: TeacherDto[];
  meta: TeacherListMeta;
}

/** The filters + pagination the web asks for. */
export interface TeachersQuery {
  venueId?: number;
  q?: string;
  /** ADDITIVE (v1.8.9, T.Z56) — «Отдел»: `OP` | `OS` | `NONE` | `MIXED`, server-side. */
  department?: TeacherDepartment;
  /**
   * ADDITIVE (v1.9.17, T.Z89) — the status filter, ONE value or a list (the
   * «ожидают подтверждения» chip asks for `['UNCONFIRMED','FOR_DELETION']`); the wire
   * form is the comma-separated list the contract documents.
   */
  status?: TeacherStatus[];
  /** ADDITIVE (v1.9.17, T.Z90) — the provenance filter (`CURATOR` = «добавлены кураторами»). */
  createdVia?: TeacherCreatedVia;
  page?: number;
  pageSize?: number;
}

export interface TeacherCreate {
  venueId: number;
  fullName: string;
  phone?: string | null;
  email?: string | null;
}

/**
 * ADDITIVE (v1.8.9, T.Z56) — the «Отдел» bucket `?department=` takes: the department of
 * the VENUE a teacher is attached to (the managers' departments). Same vocabulary as
 * `src/departments.ts`; the api resolves it server-side into the venue set.
 */
export type TeacherDepartment = 'OP' | 'OS' | 'NONE' | 'MIXED';

/** Only provided keys change; `null` clears a field; `venueId` re-assigns. */
export interface TeacherPatch extends Partial<TeacherAttributes> {
  venueId?: number;
  fullName?: string;
  phone?: string | null;
  email?: string | null;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const TEACHERS_PAGE_SIZE = 50;

/**
 * `/teachers?venueId=…&q=…&page=…` with the DEFAULTS DROPPED — the same rule
 * `venuesPath` states: a default state must not leak a parameter into the URL the api
 * receives, so a `?venueId=12` deep link from the venue card round-trips byte-stable.
 */
export function teachersPath(query: TeachersQuery = {}): string {
  const page = query.page !== undefined && query.page > 1 ? query.page : undefined;
  const pageSize =
    query.pageSize !== undefined && query.pageSize !== TEACHERS_PAGE_SIZE ? query.pageSize : undefined;
  // ADDITIVE (v1.8.9, T.Z56) — `department` travels when set, dropped when it does not
  // (the same defaults-dropped rule as every other key here).
  // ADDITIVE (v1.9.17, T.Z89/T.Z90) — `status` travels as the comma-separated list the
  // contract documents (`String(['A','B'])` === `'A,B'`, exactly the wire form), and
  // `createdVia` as the bare enum token.
  return `/teachers${buildQuery({
    venueId: query.venueId,
    q: query.q,
    department: query.department,
    status: query.status,
    createdVia: query.createdVia,
    page,
    pageSize,
  })}`;
}

export async function fetchTeachers(query: TeachersQuery, signal?: AbortSignal): Promise<TeacherListResult> {
  const envelope = await requestEnvelope<{ teachers?: TeacherDto[] }>('GET', teachersPath(query), ['data', 'teachers'], {
    signal,
  });
  const teachers = envelope.data?.teachers ?? [];
  // ADDITIVE (v1.9.17, T.Z90) — `counts` rides the meta block the unwrapper already
  // carries; an api that predates v1.9.17 answers the trio only and the chips read «—».
  const counts = (envelope.meta as { counts?: TeacherListCounts } | undefined)?.counts;
  const meta = envelope.meta;
  return {
    teachers,
    meta: {
      page: meta?.page ?? query.page ?? 1,
      pageSize: meta?.pageSize ?? meta?.limit ?? query.pageSize ?? TEACHERS_PAGE_SIZE,
      total: meta?.total ?? teachers.length,
      ...(counts ? { counts } : {}),
    },
  };
}

/** Cache identity: every filter + page is part of the key, so a change refetches. */
export function teachersQueryKey(query: TeachersQuery): readonly [
  string,
  {
    venueId: number | null;
    q: string | null;
    department: string | null;
    status: string | null;
    createdVia: string | null;
    page: number;
  },
] {
  return [
    'teachers',
    {
      venueId: query.venueId ?? null,
      q: query.q ?? null,
      department: query.department ?? null,
      status: query.status ? [...query.status].sort().join(',') : null,
      createdVia: query.createdVia ?? null,
      page: query.page ?? 1,
    },
  ];
}

/**
 * One query per (venueId, q, page). `placeholderData: keepPreviousData` keeps the
 * previous rows on screen while the next page loads — the venues/quotas rule: a filter
 * keystroke must not collapse the table into a spinner.
 */
export function useTeachers(query: TeachersQuery) {
  return useQuery({
    queryKey: teachersQueryKey(query),
    queryFn: ({ signal }) => fetchTeachers(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

export async function createTeacher(body: TeacherCreate, signal?: AbortSignal): Promise<TeacherDto> {
  const envelope = await requestEnvelope<TeacherDto>('POST', '/teachers', ['data'], {
    signal,
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return envelope.data;
}

export async function patchTeacher(id: number, patch: TeacherPatch, signal?: AbortSignal): Promise<TeacherDto> {
  const envelope = await requestEnvelope<TeacherDto>('PATCH', `/teachers/${id}`, ['data'], {
    signal,
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return envelope.data;
}

export async function deleteTeacher(id: number): Promise<void> {
  await requestEnvelope<undefined>('DELETE', `/teachers/${id}`, ['data'], { method: 'DELETE' });
}

/**
 * Everything the list has to refetch after a write: the roster page, whatever its
 * filter. Returned as a promise so a caller that is about to unmount (the T.R7 drawer
 * closes itself once its write landed) can AWAIT the refetch first — the mutation's own
 * `onSuccess` never runs in that case, because its observer is gone by then.
 */
export function invalidateTeachers(client: ReturnType<typeof useQueryClient>): Promise<void> {
  return client.invalidateQueries({ queryKey: ['teachers'] }).then(() => undefined);
}

/** The create — the answer is pushed into the current page's cache, then refetched. */
export function useCreateTeacher() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TeacherCreate) => createTeacher(body),
    onSuccess: () => invalidateTeachers(client),
  });
}

/** The edit + the venue re-assignment — one hook, because the api has one endpoint. */
export function usePatchTeacher() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TeacherPatch }) => patchTeacher(id, patch),
    onSuccess: async () => {
      await invalidateTeachers(client);
    },
  });
}

/** The delete (ADDITIVE v1.9.12, T.Z80 — MANAGER+ staff, a КУРАТОР on its own venues). */
export function useDeleteTeacher() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTeacher(id),
    onSuccess: async () => {
      await invalidateTeachers(client);
    },
  });
}
